<template>
    <div class="twitter-oauth-manager">
        <div class="header">
            <h2>🐦 Twitter OAuth Users Management</h2>
            <div class="auth-controls">
                <div class="app-selector">
                    <label for="appVersion">Twitter App:</label>
                    <select 
                        id="appVersion" 
                        v-model="selectedAppVersion" 
                        :disabled="isAuthenticating"
                        class="app-select"
                    >
                        <option value="1">App 1 (Main)</option>
                        <option value="2">App 2 (Secondary)</option>
                    </select>
                </div>
                <button 
                    class="btn-primary" 
                    @click="authenticateNewUser" 
                    :disabled="isAuthenticating"
                >
                    {{ isAuthenticating ? 'Authenticating...' : '➕ Authenticate New User' }}
                </button>
            </div>
        </div>

        <!-- Success/Error Messages -->
        <div v-if="message" class="message" :class="messageType">
            {{ message }}
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="loading">
            Loading OAuth users...
        </div>

        <!-- Users List -->
        <div v-else-if="users.length > 0" class="users-list">
            <div v-for="user in users" :key="user.user_id" class="user-card">
                <div class="user-info">
                    <div class="user-avatar">
                        <img v-if="user.profile_image_url" :src="user.profile_image_url" :alt="user.name" />
                        <div v-else class="default-avatar">{{ user.name.charAt(0) }}</div>
                    </div>
                    <div class="user-details">
                        <h3>{{ user.name }}</h3>
                        <p class="username">@{{ user.username }}</p>
                        <div class="user-meta">
                            <span class="status-badge" :class="getStatusClass(user.status)">
                                {{ getStatusText(user.status) }}
                            </span>
                            <span class="date">Added: {{ formatDate(user.created_at) }}</span>
                            <span v-if="user.last_used" class="date">Last used: {{ formatDate(user.last_used) }}</span>
                        </div>
                        <div class="scopes">
                            <span v-for="scope in user.scope" :key="scope" class="scope-badge">
                                {{ scope }}
                            </span>
                        </div>
                    </div>
                </div>
                <div class="user-actions">
                    <button 
                        class="btn-secondary" 
                        @click="testTweet(user)" 
                        :disabled="user.status !== 'active' || isTestingTweet[user.user_id]"
                    >
                        {{ isTestingTweet[user.user_id] ? 'Testing...' : '🧪 Test Tweet' }}
                    </button>
                    <button 
                        class="btn-danger" 
                        @click="revokeUser(user)" 
                        :disabled="isRevokingUser[user.user_id]"
                    >
                        {{ isRevokingUser[user.user_id] ? 'Revoking...' : '🗑️ Revoke' }}
                    </button>
                </div>
            </div>
        </div>

        <!-- Empty State -->
        <div v-else class="empty-state">
            <div class="empty-icon">🐦</div>
            <h3>No Twitter OAuth Users</h3>
            <p>Click "Authenticate New User" to add your first Twitter account.</p>
        </div>

        <!-- Test Tweet Modal -->
        <div v-if="showTestModal" class="modal-overlay" @click="closeTestModal">
            <div class="modal" @click.stop>
                <div class="modal-header">
                    <h3>Test Tweet for @{{ selectedUser?.username }}</h3>
                    <button class="close-btn" @click="closeTestModal">×</button>
                </div>
                <div class="modal-body">
                    <label for="testMessage">Tweet Message:</label>
                    <textarea 
                        id="testMessage"
                        v-model="testMessage" 
                        placeholder="Enter your test message..."
                        maxlength="280"
                    ></textarea>
                    <div class="character-count">{{ 280 - testMessage.length }} characters remaining</div>
                </div>
                <div class="modal-footer">
                    <button class="btn-secondary" @click="closeTestModal">Cancel</button>
                    <button 
                        class="btn-primary" 
                        @click="sendTestTweet" 
                        :disabled="!testMessage.trim() || isSendingTweet"
                    >
                        {{ isSendingTweet ? 'Sending...' : 'Send Tweet' }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';

export default {
    name: 'TwitterOAuthManager',
    emits: ['show-toast'],
    data() {
        return {
            users: [],
            loading: false,
            message: '',
            messageType: '',
            isAuthenticating: false,
            isTestingTweet: {},
            isRevokingUser: {},
            showTestModal: false,
            selectedUser: null,
            testMessage: 'Test tweet from ChatPTS OAuth integration 🐦',
            isSendingTweet: false,
            selectedAppVersion: 1  // Default to App 1
        };
    },
    methods: {
        async fetchUsers() {
            this.loading = true;
            try {
                const response = await axios.get(
                    `${process.env.VUE_APP_BASE_URL}/api/v2/twitter-oauth-v2/users`,
                    { withCredentials: true }
                );
                this.users = response.data.users;
                this.showMessage('Users loaded successfully', 'success');
            } catch (error) {
                console.error('Error fetching OAuth users:', error);
                this.showMessage('Failed to load OAuth users', 'error');
            } finally {
                this.loading = false;
            }
        },

        async authenticateNewUser() {
            this.isAuthenticating = true;
            try {
                const response = await axios.get(
                    `${process.env.VUE_APP_BASE_URL}/api/v2/twitter-oauth-v2/authorize`,
                    {
                        withCredentials: true,
                        params: {
                            app_version: this.selectedAppVersion
                        }
                    }
                );
                
                // Open OAuth URL in a new window
                const authWindow = window.open(
                    response.data.authorization_url,
                    'twitterAuth',
                    'width=600,height=700,scrollbars=yes,resizable=yes'
                );

                // Check if window is closed (completed or cancelled)
                const checkClosed = setInterval(() => {
                    if (authWindow.closed) {
                        clearInterval(checkClosed);
                        this.isAuthenticating = false;
                        // Refresh users list
                        this.fetchUsers();
                    }
                }, 1000);

                this.showMessage(`Opening Twitter authentication window for App ${this.selectedAppVersion}...`, 'info');
            } catch (error) {
                console.error('Error starting OAuth flow:', error);
                this.showMessage('Failed to start authentication', 'error');
                this.isAuthenticating = false;
            }
        },

        testTweet(user) {
            this.selectedUser = user;
            this.showTestModal = true;
        },

        async sendTestTweet() {
            if (!this.selectedUser || !this.testMessage.trim()) return;

            this.isSendingTweet = true;
            try {
                const response = await axios.post(
                    `${process.env.VUE_APP_BASE_URL}/api/v2/twitter-oauth-v2/test/${this.selectedUser.user_id}`,
                    { message: this.testMessage },
                    { withCredentials: true }
                );

                if (response.data.success) {
                    this.showMessage(`Test tweet posted successfully! ${response.data.tweet_url}`, 'success');
                    this.closeTestModal();
                } else {
                    this.showMessage('Failed to post test tweet', 'error');
                }
            } catch (error) {
                console.error('Error sending test tweet:', error);
                this.showMessage('Failed to post test tweet', 'error');
            } finally {
                this.isSendingTweet = false;
            }
        },

        async revokeUser(user) {
            if (!confirm(`Are you sure you want to revoke access for @${user.username}?`)) {
                return;
            }

            this.isRevokingUser = { ...this.isRevokingUser, [user.user_id]: true };
            try {
                const response = await axios.delete(
                    `${process.env.VUE_APP_BASE_URL}/api/v2/twitter-oauth-v2/users/${user.user_id}`,
                    { withCredentials: true }
                );

                if (response.data.success) {
                    this.showMessage(`Access revoked for @${user.username}`, 'success');
                    this.fetchUsers(); // Refresh the list
                } else {
                    this.showMessage('Failed to revoke user access', 'error');
                }
            } catch (error) {
                console.error('Error revoking user:', error);
                this.showMessage('Failed to revoke user access', 'error');
            } finally {
                this.isRevokingUser = { ...this.isRevokingUser, [user.user_id]: false };
            }
        },

        closeTestModal() {
            this.showTestModal = false;
            this.selectedUser = null;
            this.testMessage = 'Test tweet from ChatPTS OAuth integration 🐦';
            this.isSendingTweet = false;
        },

        showMessage(text, type) {
            this.message = text;
            this.messageType = type;
            setTimeout(() => {
                this.message = '';
                this.messageType = '';
            }, 5000);
            
            this.$emit('show-toast', text);
        },

        getStatusClass(status) {
            const classes = {
                'active': 'status-active',
                'inactive': 'status-inactive',
                'expired': 'status-expired',
                'error': 'status-error'
            };
            return classes[status] || 'status-unknown';
        },

        getStatusText(status) {
            const texts = {
                'active': 'Active',
                'inactive': 'Inactive',
                'expired': 'Expired',
                'error': 'Error'
            };
            return texts[status] || 'Unknown';
        },

        formatDate(dateString) {
            if (!dateString) return 'N/A';
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        }
    },

    mounted() {
        this.fetchUsers();
        
        // Check for OAuth callback results in URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('oauth_success') === 'true') {
            const username = urlParams.get('username');
            this.showMessage(`Successfully authenticated @${username}!`, 'success');
            // Clean up URL
            window.history.replaceState({}, document.title, window.location.pathname);
        } else if (urlParams.get('oauth_error')) {
            const error = urlParams.get('oauth_error');
            this.showMessage(`Authentication failed: ${error}`, 'error');
            // Clean up URL
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    }
};
</script>

<style scoped>
.twitter-oauth-manager {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px;
    background: rgba(50, 50, 50, 0.7);
    border-radius: 10px;
    margin-bottom: 10px;
}

.header h2 {
    color: #fff;
    margin: 0;
}

.auth-controls {
    display: flex;
    align-items: center;
    gap: 20px;
}

.app-selector {
    display: flex;
    align-items: center;
    gap: 8px;
}

.app-selector label {
    color: #fff;
    font-weight: 500;
    font-size: 14px;
}

.app-select {
    padding: 8px 12px;
    border: 1px solid #555;
    border-radius: 4px;
    background: #333;
    color: #fff;
    font-size: 14px;
    cursor: pointer;
    transition: border-color 0.3s ease;
}

.app-select:hover:not(:disabled) {
    border-color: #1da1f2;
}

.app-select:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.btn-primary, .btn-secondary, .btn-danger {
    padding: 10px 20px;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-weight: 500;
    transition: all 0.3s ease;
}

.btn-primary {
    background-color: #1da1f2;
    color: white;
}

.btn-primary:hover:not(:disabled) {
    background-color: #0d8bd9;
}

.btn-secondary {
    background-color: #666;
    color: white;
}

.btn-secondary:hover:not(:disabled) {
    background-color: #555;
}

.btn-danger {
    background-color: #dc3545;
    color: white;
}

.btn-danger:hover:not(:disabled) {
    background-color: #c82333;
}

.btn-primary:disabled, .btn-secondary:disabled, .btn-danger:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.message {
    padding: 12px 20px;
    border-radius: 6px;
    font-weight: 500;
}

.message.success {
    background-color: rgba(40, 167, 69, 0.2);
    color: #28a745;
    border: 1px solid #28a745;
}

.message.error {
    background-color: rgba(220, 53, 69, 0.2);
    color: #dc3545;
    border: 1px solid #dc3545;
}

.message.info {
    background-color: rgba(23, 162, 184, 0.2);
    color: #17a2b8;
    border: 1px solid #17a2b8;
}

.loading {
    text-align: center;
    padding: 40px;
    color: #fff;
    font-size: 18px;
}

.users-list {
    flex: 1;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 15px;
    max-height: 500px;
}

.user-card {
    background: rgba(50, 50, 50, 0.7);
    border-radius: 12px;
    padding: 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    transition: transform 0.2s ease;
}

.user-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.user-info {
    display: flex;
    gap: 15px;
    align-items: center;
    flex: 1;
}

.user-avatar img, .default-avatar {
    width: 50px;
    height: 50px;
    border-radius: 50%;
}

.default-avatar {
    background-color: #1da1f2;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: bold;
    font-size: 20px;
}

.user-details h3 {
    margin: 0 0 5px 0;
    color: #fff;
    font-size: 18px;
}

.username {
    margin: 0 0 10px 0;
    color: #1da1f2;
    font-weight: 500;
}

.user-meta {
    display: flex;
    gap: 15px;
    margin-bottom: 8px;
}

.status-badge {
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
}

.status-active { background-color: #28a745; color: white; }
.status-inactive { background-color: #6c757d; color: white; }
.status-expired { background-color: #ffc107; color: black; }
.status-error { background-color: #dc3545; color: white; }

.date {
    font-size: 12px;
    color: #ccc;
}

.scopes {
    display: flex;
    gap: 5px;
    flex-wrap: wrap;
}

.scope-badge {
    background-color: rgba(29, 161, 242, 0.2);
    color: #1da1f2;
    padding: 2px 6px;
    border-radius: 3px;
    font-size: 11px;
}

.user-actions {
    display: flex;
    gap: 10px;
}

.empty-state {
    text-align: center;
    padding: 60px 20px;
    color: #ccc;
}

.empty-icon {
    font-size: 64px;
    margin-bottom: 20px;
}

.empty-state h3 {
    margin: 0 0 10px 0;
    color: #fff;
}

.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.8);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
}

.modal {
    background: #333;
    border-radius: 12px;
    width: 90%;
    max-width: 500px;
    max-height: 90vh;
    overflow-y: auto;
}

.modal-header {
    padding: 20px;
    border-bottom: 1px solid #555;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.modal-header h3 {
    margin: 0;
    color: #fff;
}

.close-btn {
    background: none;
    border: none;
    font-size: 24px;
    color: #ccc;
    cursor: pointer;
}

.modal-body {
    padding: 20px;
}

.modal-body label {
    display: block;
    margin-bottom: 8px;
    color: #fff;
    font-weight: 500;
}

.modal-body textarea {
    width: 100%;
    min-height: 100px;
    padding: 12px;
    border: 1px solid #555;
    border-radius: 6px;
    background: #222;
    color: #fff;
    resize: vertical;
    font-family: inherit;
}

.character-count {
    text-align: right;
    margin-top: 5px;
    font-size: 12px;
    color: #ccc;
}

.modal-footer {
    padding: 20px;
    border-top: 1px solid #555;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
}
</style>
